import React, { type ReactElement, useDeferredValue, useMemo, useRef, useState } from 'react';
import VpMapPinIcon from '@vtmn-play/icons/react/VpMapPinIcon';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import clsx from 'clsx';

import { selectAllowedStores } from '../../../store/store-list';
import { cleanBeforeReload } from '../../../utils/redirectToFed';
import { ButtonDropdown } from '../../shared/Dropdowns/ButtonDropdown';
import { SearchBarVp } from '../../shared/Searches/SearchBar-VP';
import { TertiaryButton } from '../../shared/Buttons/TertiaryButton';
import { selectSelectedSite } from '../../../store/selectors/userInfo';
import { Typography } from '../../shared/Typographies/Typography';

import styles from './stores-dropdown.module.css';

const replaceLatin = /[\u0300-\u036f]/g;
const replaceSpecial = /'|\/\|_|,|\.|\(|\)/g;
const normalizationFormCanonicalDecomposition = 'NFD';

const normalizeSearch = (txt: string) => txt.normalize(normalizationFormCanonicalDecomposition).replace(replaceLatin, '').replace(replaceSpecial, '');

export const StoresDropdown = () => {
  const [storeInput, setStoreInput] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const selectedSite = useSelector(selectSelectedSite);
  const allowedStores = useSelector(selectAllowedStores);
  const deferredSearch = useDeferredValue(storeInput);

  const setChosenStore = (storeName: string) => {
    const store = allowedStores.find(site => site.id === Number(storeName.split('-')[0].trim()) || site.name === storeName);

    if (store && store.id !== selectedSite.id) {
      void cleanBeforeReload(null, store.id);
    }
  };

  const handleStoreSearch = (input: string) => {
    const findStore = allowedStores.find(store => store.id.toString() === input || store.name === input.toUpperCase());

    if (findStore) {
      void cleanBeforeReload(null, findStore.id);
    }
  };

  const stores = useMemo(() => {
    const searchedStore = new RegExp(deferredSearch || '.*', 'i');
    const storesList: ReactElement[] = [];

    allowedStores.forEach(store => {
      const txt = `${store.id} - ${store.name}`;
      const normalizedStoreInput = normalizeSearch(txt);
      if (searchedStore.test(normalizedStoreInput)) {
        storesList.push(
          <TertiaryButton
            key={store.id}
            className={clsx(styles.storeListItem, 'text-black', styles.button, { [styles.selectedSite]: store.id === selectedSite.id })}
            data-testid="store-list-item"
            size="small"
            shape="squared"
            onClick={() => setChosenStore(store.name)}
            aria-current={store.id === selectedSite.id ? 'true' : undefined}
          >
            {txt}
          </TertiaryButton>
        );
      }
    });
    return storesList;
  }, [deferredSearch, allowedStores]);

  if (allowedStores.length > 1) {
    return (
      <ButtonDropdown
        className="text-black"
        label={selectedSite.name}
        startSlot={<VpMapPinIcon />}
        data-testid="dropdown-stores"
        aria-label={t('toolbar.top.userinfos.placeholder')}
        onKeyDown={event => {
          if (event.key.length === 1) {
            searchInputRef.current?.focus();
          }
        }}
      >
        <div className={styles.storeDropdownWrapper}>
          <SearchBarVp
            ref={searchInputRef}
            className={styles.storeSearchBar}
            id="stores-list-search-bar"
            placeholder={t('toolbar.top.userinfos.placeholder')}
            value={storeInput}
            onChange={event => setStoreInput(event.target.value)}
            onSearch={handleStoreSearch}
            data-testid="stores-list-search-bar"
          />

          {stores}
        </div>
      </ButtonDropdown>
    );
  }

  return (
    <div className={styles.selectedSiteContainer}>
      <VpMapPinIcon size={16} />
      <Typography.Body color="neutral" size="caption" as="span" className="selected-site">
        {selectedSite.name}
      </Typography.Body>
    </div>
  );
};
